import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SegmentedPills from "../ui/SegmentedPills";
import { api, formatEURDecimal, formatDateFR, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, X, FileText, Paperclip, ShoppingCart, Trash2, CheckCircle2, ExternalLink } from "lucide-react";
import { confirmAction } from "../ConfirmDialog";

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none";
const labelCls = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";

export const PURCHASE_CATEGORY_LABELS = {
  carburant: "Carburant",
  entretien_reparation: "Entretien / réparation",
  assurance: "Assurance",
  leasing_financement: "Leasing / financement",
  peages_stationnement: "Péages / stationnement",
  nettoyage: "Nettoyage",
  fournitures: "Fournitures",
  taxes: "Taxes",
  autre: "Autre",
};

export const PURCHASE_METHOD_LABELS = {
  virement: "Virement", cb: "Carte bancaire", especes: "Espèces", cheque: "Chèque", prelevement: "Prélèvement", autre: "Autre",
};

// ─── Facture libre : bouton + formulaire inline (même moteur que les factures résa) ───
export function FreeInvoiceForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [client, setClient] = useState({ name: "", email: "", phone: "" });
  const [lines, setLines] = useState([{ label: "", amount: "" }]);
  const [saving, setSaving] = useState(false);

  const total = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/vendor/invoices/free", {
        client_name: client.name,
        client_email: client.email,
        client_phone: client.phone,
        lines: lines.filter((l) => l.label.trim()).map((l) => ({ label: l.label, amount: parseFloat(l.amount) || 0 })),
      });
      toast.success(`Facture ${data.invoice.reference} créée`);
      setOpen(false);
      setClient({ name: "", email: "", phone: "" });
      setLines([{ label: "", amount: "" }]);
      onCreated?.(data.invoice);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700"
        data-testid="add-free-invoice-btn"
      >
        {open ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {open ? "Fermer" : "Facture libre"}
      </button>
      {open && (
        <form onSubmit={submit} className="mt-4 rounded-2xl border border-violet-200 bg-violet-50/30 p-4 space-y-3" data-testid="free-invoice-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Client *</label>
              <input required value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} placeholder="Nom / société" className={inputCls + " mt-1"} data-testid="free-invoice-client-name" />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} className={inputCls + " mt-1"} data-testid="free-invoice-client-email" />
            </div>
            <div>
              <label className={labelCls}>Téléphone</label>
              <input value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} className={inputCls + " mt-1"} data-testid="free-invoice-client-phone" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Lignes de facturation (TTC)</label>
            <div className="space-y-2 mt-1">
              {lines.map((l, i) => (
                <div key={i} className="flex gap-2">
                  <input required={i === 0} value={l.label} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                    placeholder="Désignation" className={inputCls + " flex-1"} data-testid={`free-invoice-line-label-${i}`} />
                  <input required={i === 0} type="number" step="0.01" value={l.amount} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
                    placeholder="€" className={inputCls + " w-28"} data-testid={`free-invoice-line-amount-${i}`} />
                  {lines.length > 1 && (
                    <button type="button" onClick={() => setLines(lines.filter((_, j) => j !== i))} className="h-[38px] w-9 rounded-xl border border-slate-200 text-slate-400 hover:text-red-500 hover:bg-red-50 inline-flex items-center justify-center shrink-0">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setLines([...lines, { label: "", amount: "" }])} className="mt-2 text-xs font-semibold text-violet-600 hover:underline" data-testid="free-invoice-add-line">
              + Ajouter une ligne
            </button>
          </div>
          <div className="flex items-center justify-between gap-3 pt-2 border-t border-violet-100">
            <div className="text-sm text-slate-600">Total TTC : <span className="font-semibold text-slate-900" data-testid="free-invoice-total">{formatEURDecimal(total)}</span> <span className="text-[10px] text-slate-400">(TVA & numérotation identiques aux factures de réservation)</span></div>
            <button type="submit" disabled={saving} className="h-[38px] px-6 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2" data-testid="free-invoice-submit">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Émettre la facture
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Achats fournisseurs (ACH-) ───────────────────────────────────────────────
const EMPTY_PURCHASE = { supplier_id: "", label: "", date: "", amount_ttc: "", amount_ht: "", category: "autre", payment_method: "virement", status: "to_pay", notes: "" };

export function PurchasesSection({ onChanged }) {
  const [purchases, setPurchases] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_PURCHASE);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/vendor/purchases").then((r) => { setPurchases(r.data.purchases || []); onChanged?.(); }).catch(() => setPurchases([]));
  useEffect(() => {
    load();
    api.get("/vendor/suppliers").then((r) => setSuppliers(r.data.suppliers || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.supplier_id) { toast.error("Sélectionnez un fournisseur de l'annuaire"); return; }
    setSaving(true);
    try {
      await api.post("/vendor/purchases", {
        ...form,
        amount_ttc: parseFloat(form.amount_ttc),
        amount_ht: form.amount_ht === "" ? null : parseFloat(form.amount_ht),
      });
      toast.success("Achat enregistré");
      setForm(EMPTY_PURCHASE);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  const setStatus = async (p, status) => {
    try {
      await api.patch(`/vendor/purchases/${p.id}`, { status });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const remove = async (p) => {
    if (!(await confirmAction({
      title: "Supprimer cet achat ?",
      message: `L'achat ${p.reference} sera définitivement supprimé de votre suivi de dépenses.`,
      confirmLabel: "Supprimer l'achat",
      variant: "danger",
    }))) return;
    try {
      await api.delete(`/vendor/purchases/${p.id}`);
      toast.success("Achat supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const uploadDoc = async (p, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vendor/purchases/${p.id}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Justificatif ajouté");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const openDoc = async (path) => {
    try {
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Impossible d'ouvrir le justificatif");
    }
  };

  const totals = useMemo(() => {
    const list = purchases || [];
    return {
      toPay: list.filter((p) => p.status === "to_pay").reduce((s, p) => s + (p.amount_ttc || 0), 0),
      paid: list.filter((p) => p.status === "paid").reduce((s, p) => s + (p.amount_ttc || 0), 0),
    };
  }, [purchases]);

  const filtered = useMemo(() => {
    if (!purchases) return [];
    if (statusFilter === "all") return purchases;
    return purchases.filter((p) => p.status === statusFilter);
  }, [purchases, statusFilter]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mt-6" data-testid="purchases-section">
      <div className="p-6 border-b border-slate-100">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="overline">Achats fournisseurs</div>
            <h3 className="font-heading text-xl font-medium tracking-tight text-slate-900">Dépenses (ACH-)</h3>
          </div>
          <div className="flex items-center gap-3">
            <SegmentedPills
              value={statusFilter}
              onChange={setStatusFilter}
              items={[
                { key: "all", label: "Tous" },
                { key: "to_pay", label: "À payer" },
                { key: "paid", label: "Payés" },
              ]}
              testid="purchases-status-filter"
            />
            <button onClick={() => setShowForm(!showForm)} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700" data-testid="add-purchase-btn">
              {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showForm ? "Fermer" : "Nouvel achat"}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4 max-w-md">
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3" data-testid="purchases-total-topay">
            <div className={labelCls}>À payer</div>
            <div className="font-heading text-xl font-medium text-amber-700 mt-0.5">{formatEURDecimal(totals.toPay)}</div>
          </div>
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3" data-testid="purchases-total-paid">
            <div className={labelCls}>Payé</div>
            <div className="font-heading text-xl font-medium text-emerald-700 mt-0.5">{formatEURDecimal(totals.paid)}</div>
          </div>
        </div>
        {showForm && (
          <form onSubmit={submit} className="mt-4 rounded-2xl border border-violet-200 bg-violet-50/30 p-4 grid sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="purchase-form">
            <div>
              <label className={labelCls}>Fournisseur (annuaire) *</label>
              <select required value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className={inputCls + " mt-1"} data-testid="purchase-supplier">
                <option value="">Sélectionner…</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.reference} — {s.trade_name || s.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Libellé *</label>
              <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Pneus hiver x4" className={inputCls + " mt-1"} data-testid="purchase-label" />
            </div>
            <div>
              <label className={labelCls}>Date *</label>
              <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls + " mt-1"} data-testid="purchase-date" />
            </div>
            <div>
              <label className={labelCls}>Catégorie comptable</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls + " mt-1"} data-testid="purchase-category">
                {Object.entries(PURCHASE_CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Montant TTC (€) *</label>
              <input type="number" min="0.01" step="0.01" required value={form.amount_ttc} onChange={(e) => setForm({ ...form, amount_ttc: e.target.value })} className={inputCls + " mt-1"} data-testid="purchase-amount-ttc" />
            </div>
            <div>
              <label className={labelCls}>Montant HT (€)</label>
              <input type="number" min="0" step="0.01" value={form.amount_ht} onChange={(e) => setForm({ ...form, amount_ht: e.target.value })} className={inputCls + " mt-1"} data-testid="purchase-amount-ht" />
            </div>
            <div>
              <label className={labelCls}>Mode de paiement</label>
              <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className={inputCls + " mt-1"} data-testid="purchase-method">
                {Object.entries(PURCHASE_METHOD_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Statut</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls + " mt-1"} data-testid="purchase-status">
                <option value="to_pay">À payer</option>
                <option value="paid">Payé</option>
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className={labelCls}>Notes</label>
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls + " mt-1"} data-testid="purchase-notes" />
            </div>
            <div className="flex items-end">
              <button type="submit" disabled={saving} className="w-full h-[38px] rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="purchase-submit">
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Enregistrer
              </button>
            </div>
          </form>
        )}
      </div>

      {purchases === null ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-400" data-testid="purchases-empty">
          {purchases.length === 0 ? "Aucun achat fournisseur enregistré." : "Aucun achat pour ce filtre."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-6 py-3 font-semibold">Référence</th>
                <th className="text-left px-6 py-3 font-semibold">Libellé</th>
                <th className="text-left px-6 py-3 font-semibold">Fournisseur</th>
                <th className="text-left px-6 py-3 font-semibold">Date</th>
                <th className="text-left px-6 py-3 font-semibold">Catégorie</th>
                <th className="text-left px-6 py-3 font-semibold">Statut</th>
                <th className="text-right px-6 py-3 font-semibold">TTC</th>
                <th className="text-right px-6 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-violet-50/30 transition-colors" data-testid={`purchase-row-${p.reference}`}>
                  <td className="px-6 py-3.5 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
                      <ShoppingCart className="h-3.5 w-3.5 text-violet-600" /> {p.reference}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 max-w-[200px]">
                    <span className="truncate block text-slate-700">{p.label}</span>
                    {(p.documents || []).length > 0 && (
                      <button onClick={() => openDoc(p.documents[0].path)} className="inline-flex items-center gap-1 text-[10px] text-violet-600 hover:underline" data-testid={`purchase-doc-open-${p.reference}`}>
                        <FileText className="h-2.5 w-2.5" /> {p.documents[0].name || "Justificatif"} <ExternalLink className="h-2.5 w-2.5" />
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-3.5 max-w-[160px] truncate text-slate-600">{p.supplier_name}</td>
                  <td className="px-6 py-3.5 whitespace-nowrap text-slate-500 text-xs">{formatDateFR(p.date)}</td>
                  <td className="px-6 py-3.5 whitespace-nowrap text-xs text-slate-600">{PURCHASE_CATEGORY_LABELS[p.category] || p.category}</td>
                  <td className="px-6 py-3.5 whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${p.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {p.status === "paid" ? "Payé" : "À payer"}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 text-right font-semibold text-slate-900">{formatEURDecimal(p.amount_ttc)}</td>
                  <td className="px-6 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <label className="h-7 w-7 rounded-md hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center cursor-pointer" title="Ajouter un justificatif" data-testid={`purchase-add-doc-${p.reference}`}>
                        <Paperclip className="h-3.5 w-3.5" />
                        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadDoc(p, e.target.files[0]); e.target.value = ""; }} />
                      </label>
                      {p.status === "to_pay" ? (
                        <button onClick={() => setStatus(p, "paid")} className="h-7 px-2 rounded-md hover:bg-emerald-50 text-emerald-600 text-[10px] font-semibold inline-flex items-center gap-1" data-testid={`purchase-mark-paid-${p.reference}`}>
                          <CheckCircle2 className="h-3 w-3" /> Payé
                        </button>
                      ) : (
                        <button onClick={() => setStatus(p, "to_pay")} className="h-7 px-2 rounded-md hover:bg-slate-100 text-slate-500 text-[10px] font-semibold" data-testid={`purchase-mark-topay-${p.reference}`}>
                          À payer
                        </button>
                      )}
                      <button onClick={() => remove(p)} className="h-7 w-7 rounded-md hover:bg-red-50 text-red-500 inline-flex items-center justify-center" data-testid={`purchase-delete-${p.reference}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
